import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const FiltroData = ({ value, loading, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const optionRefs = useRef([]);
  const menuId = useId();
  
  const presets = useMemo(() => [
    {
      id: 'diario',
      label: 'Diário',
      getDates: () => {
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(today), end: toISO(today) };
      }
    },
    {
      id: 'esta_semana',
      label: 'Esta Semana',
      getDates: () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Segunda-feira
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Domingo
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfWeek), end: toISO(endOfWeek) };
      }
    },
    {
      id: 'mes_atual',
      label: 'Mês Atual',
      getDates: () => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfMonth), end: toISO(endOfMonth) };
      }
    },
    {
      id: 'mes_anterior',
      label: 'Mês Anterior',
      getDates: () => {
        const today = new Date();
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfLastMonth), end: toISO(endOfLastMonth) };
      }
    },
    {
      id: 'trimestre_atual',
      label: 'Trimestre Atual',
      getDates: () => {
        const today = new Date();
        const currentMonth = today.getMonth();
        const quarterStart = Math.floor(currentMonth / 3) * 3;
        const startOfQuarter = new Date(today.getFullYear(), quarterStart, 1);
        const endOfQuarter = new Date(today.getFullYear(), quarterStart + 3, 0);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfQuarter), end: toISO(endOfQuarter) };
      }
    },
    {
      id: 'ano_atual',
      label: 'Ano Atual',
      getDates: () => {
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const endOfYear = new Date(today.getFullYear(), 11, 31);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(startOfYear), end: toISO(endOfYear) };
      }
    },
    {
      id: 'ultimos_6_meses',
      label: 'Últimos 6 Meses',
      getDates: () => {
        const today = new Date();
        const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        const pad = (n) => String(n).padStart(2, '0');
        const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        return { start: toISO(sixMonthsAgo), end: toISO(today) };
      }
    },
    {
      id: 'personalizado',
      label: 'Data Personalizada',
      getDates: () => value
    }
  ], [value]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const selectedIndex = presets.findIndex((preset) => preset.id === value.presetId);
    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [isOpen, presets, value.presetId]);

  useEffect(() => {
    if (!isOpen) return;
    optionRefs.current[focusedIndex]?.focus();
  }, [focusedIndex, isOpen]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const handlePresetClick = (preset) => {
    if (preset.id === 'personalizado') {
      // Não faz nada, apenas mantém o dropdown aberto
      return;
    }
    
    const dates = preset.getDates();
    onChange({ presetId: preset.id, ...dates });
    setIsOpen(false);
  };

  const validateDates = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return startDate <= endDate;
  };

  const handleDateChange = (field, newValue) => {
    const newDates = { 
      ...value, 
      [field]: newValue,
      presetId: 'personalizado' // Sempre define como personalizado quando edita datas
    };
    
    // Se ambas as datas estão preenchidas, valida
    if (newDates.start && newDates.end) {
      if (validateDates(newDates.start, newDates.end)) {
        onChange(newDates);
      }
    } else {
      // Se apenas uma data está preenchida, atualiza sem validar
      onChange(newDates);
    }
  };

  const handleTriggerKeyDown = (event) => {
    if (loading) return;

    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (event, index, preset) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedIndex((index + 1) % presets.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedIndex((index - 1 + presets.length) % presets.length);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setFocusedIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setFocusedIndex(presets.length - 1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePresetClick(preset);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      buttonRef.current?.focus();
      return;
    }

    if (event.key === 'Tab') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative max-w-full overflow-visible" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
        disabled={loading}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        className="flex max-w-full items-center gap-2 rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm font-semibold text-[color:var(--text-primary)] transition-all duration-[var(--cp-motion-fast,180ms)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] disabled:opacity-50 motion-reduce:transition-none"
      >
        <Calendar className="h-4 w-4 flex-shrink-0" />
        <span className="truncate max-w-[120px] sm:max-w-32">
          {value.presetId === 'personalizado' 
            ? `${formatDate(value.start)} - ${formatDate(value.end)}`
            : presets.find(p => p.id === value.presetId)?.label || 'Mês Atual'
          }
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Overlay para fechar o dropdown - abaixo dos modais (z-40) */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown - z-50 igual aos outros componentes e modais */}
          <div
            id={menuId}
            role="menu"
            aria-label="Períodos disponíveis"
            className="absolute left-0 z-50 mt-2 w-60 max-w-[calc(100vw-1rem)] rounded-[0.8rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface,var(--surface))] [box-shadow:var(--cp-shadow-soft)] sm:left-auto sm:right-0 sm:max-w-[calc(100vw-2rem)]"
          >
            <div className="py-2">
            {presets.map((preset) => (
              <button
                key={preset.id}
                ref={(element) => {
                  const index = presets.findIndex((item) => item.id === preset.id);
                  optionRefs.current[index] = element;
                }}
                onClick={() => handlePresetClick(preset)}
                onKeyDown={(event) => handleOptionKeyDown(event, presets.findIndex((item) => item.id === preset.id), preset)}
                role="menuitemradio"
                aria-checked={value.presetId === preset.id}
                tabIndex={value.presetId === preset.id ? 0 : -1}
                className={`w-full px-4 py-2.5 text-left text-sm transition-colors duration-[var(--cp-motion-fast,180ms)] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--accent)] hover:bg-[color:var(--cp-surface-soft,var(--surface-muted))] ${
                  value.presetId === preset.id 
                    ? 'bg-[color:var(--status-info-bg,var(--surface-muted))] text-[color:var(--status-info-fg,var(--text-primary))] font-semibold [box-shadow:inset_2px_0_0_var(--accent)]'
                    : 'text-[color:var(--text-primary)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{preset.label}</span>
                  {value.presetId === preset.id && (
                    <div className="w-2 h-2 bg-[color:var(--accent)] rounded-full"></div>
                  )}
                </div>
              </button>
            ))}
          </div>
          
          <div className="border-t border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] p-4">
            <div className="text-xs text-[color:var(--text-muted)] mb-3">Consulta personalizada</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <input
                  type="date"
                  value={value.start || ''}
                  onChange={(e) => handleDateChange('start', e.target.value)}
                  className="w-full rounded-[0.6rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface,var(--surface))] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface-soft,var(--surface-muted))] focus-visible:border-[color:var(--accent)]"
                  placeholder="Início"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={value.end || ''}
                  onChange={(e) => handleDateChange('end', e.target.value)}
                  className="w-full rounded-[0.6rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface,var(--surface))] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface-soft,var(--surface-muted))] focus-visible:border-[color:var(--accent)]"
                  placeholder="Fim"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  if (value.start && value.end) {
                    setIsOpen(false);
                  }
                }}
                disabled={!value.start || !value.end}
                className="flex-1 px-3 py-2 text-sm bg-[color:var(--accent)] text-white rounded-md hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              >
                Aplicar
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="px-3 py-2 text-sm border border-[color:var(--border)] text-[color:var(--text-primary)] rounded-md hover:bg-[color:var(--surface-muted)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              >
                Cancelar
              </button>
            </div>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FiltroData;
